export const EFORM_SIDEBAR_PLACEMENT_OPTIONS = [
  { key: 'eforms_only', label: 'Only in E-Forms group' },
  { key: 'pms_after_counters', label: 'PMS > after Counters' },
  { key: 'hr_under_crew', label: 'Human Resources > Crew' },
] as const;

export type EFormSidebarPlacementKey = (typeof EFORM_SIDEBAR_PLACEMENT_OPTIONS)[number]['key'];

export type EFormSidebarPlacement =
  | {
      mode: 'catalog_only';
    }
  | {
      mode: 'shortcut';
      /** Insert this shortcut relative to this menu item's route path. */
      anchorPath: string;
      insert: 'before' | 'after';
      sortOrder?: number;
    };

export type EFormSidebarPlacementsSetting = EFormSidebarPlacement[] | EFormSidebarPlacementKey[];

export const DEFAULT_EFORM_SIDEBAR_PLACEMENTS: EFormSidebarPlacement[] = [{ mode: 'catalog_only' }];

const LEGACY_PLACEMENT_TO_ANCHOR: Record<
  EFormSidebarPlacementKey,
  { mode: 'catalog_only' } | { mode: 'shortcut'; anchorPath: string; insert: 'before' | 'after' }
> = {
  eforms_only: { mode: 'catalog_only' },
  pms_after_counters: {
    mode: 'shortcut',
    anchorPath: '/pms/pms-maintenance-counters',
    insert: 'after',
  },
  hr_under_crew: {
    mode: 'shortcut',
    anchorPath: '/crew',
    insert: 'after',
  },
};

export function buildSidebarPlacements(
  shortcuts: Extract<EFormSidebarPlacement, { mode: 'shortcut' }>[],
  includeCatalog: boolean,
): EFormSidebarPlacement[] {
  const next: EFormSidebarPlacement[] = [];
  if (includeCatalog) next.push({ mode: 'catalog_only' });
  next.push(...shortcuts);
  return next;
}

export function normalizeSidebarPlacements(
  raw: unknown,
): EFormSidebarPlacement[] {
  if (raw == null) return DEFAULT_EFORM_SIDEBAR_PLACEMENTS;
  if (!Array.isArray(raw)) return DEFAULT_EFORM_SIDEBAR_PLACEMENTS;
  if (raw.length === 0) return [];

  // Back-compat: legacy stored `string[]` of placement keys.
  if (raw.length > 0 && typeof raw[0] === 'string') {
    const keys = raw.filter((v): v is EFormSidebarPlacementKey => typeof v === 'string' && v in LEGACY_PLACEMENT_TO_ANCHOR);
    if (!keys.length) return DEFAULT_EFORM_SIDEBAR_PLACEMENTS;
    const mapped = keys.map((k) => LEGACY_PLACEMENT_TO_ANCHOR[k]).filter(Boolean);
    // eslint-disable-next-line no-restricted-syntax
    return mapped.map((p) => p as EFormSidebarPlacement);
  }

  const parsed: EFormSidebarPlacement[] = [];
  for (const entry of raw as Array<unknown>) {
    if (!entry || typeof entry !== 'object') continue;
    const e = entry as Record<string, unknown>;
    if (e.mode === 'catalog_only') {
      parsed.push({ mode: 'catalog_only' });
      continue;
    }
    if (e.mode === 'shortcut') {
      if (typeof e.anchorPath !== 'string' || !e.anchorPath) continue;
      if (e.anchorPath.startsWith('/voyage')) continue;
      const insert = e.insert === 'before' || e.insert === 'after' ? e.insert : 'after';
      const sortOrder =
        typeof e.sortOrder === 'number' && Number.isFinite(e.sortOrder) ? e.sortOrder : undefined;
      parsed.push({ mode: 'shortcut', anchorPath: e.anchorPath, insert, sortOrder });
      continue;
    }
  }

  return parsed.length ? parsed : DEFAULT_EFORM_SIDEBAR_PLACEMENTS;
}

export function hasCatalogOnly(placements: EFormSidebarPlacement[]): boolean {
  return placements.some((p) => p.mode === 'catalog_only');
}

/** Reports default to hidden until the user configures placements (unlike forms). */
export function normalizeReportSidebarPlacements(raw: unknown): EFormSidebarPlacement[] {
  if (raw == null) return [];
  if (Array.isArray(raw) && raw.length === 0) return [];
  return normalizeSidebarPlacements(raw);
}

export type SidebarVisibilityEntry = {
  kind: 'catalog' | 'shortcut' | 'hidden';
  label: string;
};

export function describeSidebarPlacements(
  raw: unknown,
  pathLabels?: Record<string, string>,
): SidebarVisibilityEntry[] {
  const placements = normalizeSidebarPlacements(raw);
  const entries: SidebarVisibilityEntry[] = [];

  if (placements.some((p) => p.mode === 'catalog_only')) {
    entries.push({ kind: 'catalog', label: 'E-Forms' });
  }

  for (const p of placements) {
    if (p.mode !== 'shortcut') continue;
    const anchor = pathLabels?.[p.anchorPath] ?? p.anchorPath;
    entries.push({
      kind: 'shortcut',
      label: `${p.insert === 'before' ? 'Before' : 'After'} · ${anchor}`,
    });
  }

  if (!entries.length) {
    entries.push({ kind: 'hidden', label: 'Hidden' });
  }

  return entries;
}

export function sidebarPlacementsFromSchema(schemaJson: unknown): unknown {
  if (!schemaJson || typeof schemaJson !== 'object') return undefined;
  const settings = (schemaJson as { settings?: { sidebarPlacements?: unknown } }).settings;
  return settings?.sidebarPlacements;
}

